/**
 * api/cip.ts — Customer Identification Program endpoints.
 *
 * Wraps the four CIP-adjacent HTTP routes:
 *
 *   * GET  /v1/legal/documents        — public; the 4 disclosures to present
 *   * POST /v1/legal/accept           — records a per-doc acceptance
 *   * GET  /v1/identity               — snapshot (status + tier + limits)
 *   * POST /v1/identity/submit        — the CIP submit; returns a verdict
 *
 * SSN HANDLING:
 *   The `submitIdentity` request body carries the full 9-digit SSN.
 *   That's the ONE place the value crosses the API boundary; nothing
 *   in this file caches, logs, or persists it. Callers MUST NOT
 *   store the returned Promise's input either — the shared
 *   `stripBannedFields` retention seam catches accidental storage.
 *
 *   The response never echoes back the SSN. `ssn_last_four` is the
 *   only remnant, matched against auth.users.ssn_last_four for the
 *   next tier-2 upgrade path.
 */

import { api, ApiClientError } from "./client";

// ---------------------------------------------------------------------
// Legal
// ---------------------------------------------------------------------

export interface LegalDocument {
  document_type: string;       // e.g. "prepaid_short_form"
  document_version: string;    // e.g. "2026-08-06-draft-01"
  title: string;
  content_markdown: string;
}

export interface LegalDocumentsOut {
  documents: LegalDocument[];
}

/** Public — no session required. Fetched before the user has a session. */
export async function getLegalDocuments(): Promise<LegalDocument[]> {
  const r = await api<LegalDocumentsOut>("/v1/legal/documents", {
    method: "GET",
  });
  return r.documents;
}

export interface LegalAcceptIn {
  document_type: string;
  document_version: string;
}

export interface LegalAcceptOut {
  accepted: boolean;
  document_type: string;
  document_version: string;
  accepted_at: string;
}

/** Session required. Idempotent — re-accepting the same (type, version) is a no-op. */
export async function acceptLegal(body: LegalAcceptIn): Promise<LegalAcceptOut> {
  return api<LegalAcceptOut>("/v1/legal/accept", {
    method: "POST",
    body,
  });
}

// ---------------------------------------------------------------------
// Identity — CIP status snapshot + submit.
//
// The response shape matches api-contract.ts::Identity. Server maps
// internal states to wire enum: `unstarted → unverified`, `submitted → verifying`.
// ---------------------------------------------------------------------

import type { IdentityStatus } from "@aster/onboarding";

export interface Money {
  amount: string;   // integer string, minor units
  asset: "USD";
}

export interface Identity {
  tier: 0 | 1 | 2 | 3;
  status: IdentityStatus;
  limits: {
    balance_cap: Money;
    monthly_volume: Money;
    per_transaction: Money;
  };
  next_tier_requirements: string[];
  // Extension fields — not in the base contract but useful.
  last_submitted_at: string | null;
  last_verdict_at: string | null;
  ssn_last_four: string | null;
  /**
   * Hosted-flow URL for providers that verify off-app (Plaid IDV's
   * shareable_url). Present ONLY on the /v1/identity/submit response.
   * When set, the client MUST redirect to it (window.location.assign);
   * on return, poll GET /v1/identity for the verdict.
   */
  action_url?: string;
}

/** Session required. */
export async function getIdentity(): Promise<Identity> {
  return api<Identity>("/v1/identity", { method: "GET" });
}

export interface IdentitySubmitIn {
  legal_name: string;
  dob: string;               // 'YYYY-MM-DD'
  address_line1: string;
  address_line2: string | null;
  city: string;
  region: string;            // state / province
  postal_code: string;
  country: "US";
  ssn: string;               // 9 digits, hyphens permitted (server normalises)
}

/**
 * The CIP submit. Server enforces:
 *   - legal_not_accepted   — client must POST /legal/accept for the
 *     4 required docs FIRST. 400 code.
 *   - already_verified     — verified users can't resubmit. 409 code.
 *   - Age gate (under-18)  — server-side, authoritative. Response is
 *     200 with `status: "blocked"`.
 *   - Provider verdict     — 200 with one of verified / pending /
 *     action_needed / rejected.
 *
 * Callers surface each of these to the router; see the signup shell.
 */
export async function submitIdentity(body: IdentitySubmitIn): Promise<Identity> {
  return api<Identity>("/v1/identity/submit", {
    method: "POST",
    body,
  });
}

/**
 * Convenience for the shell: is this the terminal "the client cannot
 * make further progress on its own" state?
 */
export function isTerminalIdentityStatus(s: IdentityStatus): boolean {
  return s === "rejected" || s === "blocked" || s === "verified";
}

export { ApiClientError };
